import { useState, useEffect, useRef } from 'react';
import { Step, Steps } from 'intro.js-react';

import 'intro.js/introjs.css';

import { useClientContext, useTranslationContext } from 'hooks';
import { getClientOptions, clientHasCustomOptions } from 'scripts/clients/index';

import './HelpTips.scss';

export default function HelpTips(props: { onBeforeVideoStep: () => void }) {
  const { onBeforeVideoStep } = props;
  const { client } = useClientContext();
  const { t } = useTranslationContext();

  const [steps, setSteps]: [Step[], (v: Step[]) => void] = useState([] as Step[]);
  const introJsRef = useRef<Steps | null>(null);

  const isCustomClient = clientHasCustomOptions(client?.id),
    disabledForCustomClient = isCustomClient ? getClientOptions(client)?.helpfulHintsEnabled : true;

  useEffect(() => {
    if (!disabledForCustomClient || localStorage.helpTipsShown) return;

    const timer = setTimeout(() => {

      const mobileWrapper = document.querySelector('.mobileWrapper'),
        isMobile = mobileWrapper && window.getComputedStyle(mobileWrapper).display !== 'none';

      const steps: Step[] = [
        {
          element: '#new_question',
          intro: `${t('Ask')} ${client?.firstName} ${t('a question')}.`,
          position: 'auto'
        },
        {
          element:  isMobile ? '#mobile_first_ask_button' : '#first_ask_button' ,
          intro: t("Join in on a question that's already been asked."),
          position: 'auto'
        },
        {
          element: '#categories_button',
          intro: t('Discover issues that are important to you.'),
          position: 'auto'
        },
        {
          element: isMobile ? '#mobile_video_player' : '#video_player',
          intro: `${t('Watch and share')} ${client?.firstName}'s ${t('videos')}.`,
          position: 'bottom-right-aligned'
        }
      ];

      if (client?.clientType === 'Government')
        steps.push(
          {
            intro: t('Take further actions.'),
            position: 'auto'
          }
        );
      else {
        document.querySelector('#donate_button') && steps.push(
          {
            element: isMobile ? '.menuOpen' : '#donate_button',
            intro: `${t('Support')} ${client?.firstName} ${t('by donating')}.`,
            position: 'auto'
          }
        );
        steps.push(
          {
            element: isMobile ? '.menuOpen' : '#volunteer_button',
            intro: t('Join the team as a volunteer.'),
            position: 'auto'
          }
        );
      }

      setSteps(steps);
    }, 1600);

    return () => clearTimeout(timer);
  }, [client, disabledForCustomClient, t]);

  if (!disabledForCustomClient || localStorage.helpTipsShown) return <></>;

  return (
    <Steps
      enabled={localStorage.getItem(`lockScreenEntered${client.id}`) !== null}
      steps={steps}
      initialStep={0}
      ref={(ref) => introJsRef.current = ref}
      onExit={() => { localStorage.setItem('helpTipsShown', 'true') }}
      onBeforeChange={(nextStepIndex) => {
        if (nextStepIndex === 2) {
          onBeforeVideoStep();
          introJsRef.current?.updateStepElement(3);
        }
      }}
      options={{
        nextLabel: t('Next'),
        doneLabel: t('Done'),
        skipLabel: t('Skip'),
        showBullets: false,
        disableInteraction: true,
        exitOnOverlayClick: false,
      }}
    />
  );
}

